// src/utils/parseOrgExcel.js
import * as XLSX from 'xlsx';

/**
 * Parse an Excel file and extract org structure data
 * @param {File} file - The Excel file to parse
 * @returns {Promise<Object>} - Object with users array and hierarchy array
 */
export const parseOrgExcel = (file) => {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();

        reader.onload = (e) => {
            try {
                const data = new Uint8Array(e.target.result);
                const workbook = XLSX.read(data, { type: 'array' });

                let users = [];
                let hierarchy = [];

                // Try to find and parse the company_structure sheet
                const structureSheetName = workbook.SheetNames.find(
                    (name) =>
                        name.toLowerCase().includes('company_structure') ||
                        name.toLowerCase().includes('struktur') ||
                        name.toLowerCase().includes('hierarchy')
                );

                if (structureSheetName) {
                    const structureSheet = workbook.Sheets[structureSheetName];
                    const structureData = XLSX.utils.sheet_to_json(structureSheet, {
                        defval: '',
                        raw: false,
                    });

                    // Parse hierarchical structure (level 0, level 1, level 2, level 3, ...)
                    hierarchy = structureData
                        .map((row) => {
                            const normalized = {};
                            Object.keys(row).forEach((key) => {
                                const lowerKey = key.toLowerCase().trim();
                                normalized[lowerKey] = row[key]?.toString().trim() || '';
                            });

                            // Extract levels dynamically
                            const levels = [];
                            for (let i = 0; i <= 10; i++) {
                                const levelKey = `level ${i}`;
                                const levelKeyAlt = `level${i}`;
                                const value = normalized[levelKey] || normalized[levelKeyAlt] || '';
                                if (value || i < 4) {
                                    levels.push(value);
                                }
                            }

                            return { levels };
                        })
                        .filter((row) => row.levels.some((l) => l)); // Filter empty rows
                }

                // Try to find and parse the users sheet
                const usersSheetName =
                    workbook.SheetNames.find(
                        (name) =>
                            name.toLowerCase().includes('user') ||
                            name.toLowerCase().includes('mitarbeiter') ||
                            name.toLowerCase().includes('benutzer')
                    ) || workbook.SheetNames[0];

                if (usersSheetName && !structureSheetName) {
                    // Fallback: use first sheet if no structure sheet found
                    const usersSheet = workbook.Sheets[usersSheetName];
                    const usersData = XLSX.utils.sheet_to_json(usersSheet, {
                        defval: '',
                        raw: false,
                    });

                    users = usersData
                        .map((row) => {
                            const normalized = {};
                            Object.keys(row).forEach((key) => {
                                const lowerKey = key.toLowerCase().trim();
                                normalized[lowerKey] = row[key]?.toString().trim() || '';
                            });

                            return {
                                email: normalized.email || normalized['e-mail'] || '',
                                lastname:
                                    normalized.lastname || normalized.nachname || normalized['last name'] || '',
                                firstname:
                                    normalized.firstname || normalized.vorname || normalized['first name'] || '',
                                unit: normalized.unit || normalized.einheit || normalized.abteilung || '',
                                dashboard: normalized.dashboard || '',
                            };
                        })
                        .filter((row) => row.email);
                } else if (usersSheetName) {
                    // Parse users sheet alongside structure
                    const usersSheet = workbook.Sheets[usersSheetName];
                    const usersData = XLSX.utils.sheet_to_json(usersSheet, {
                        defval: '',
                        raw: false,
                    });

                    users = usersData
                        .map((row) => {
                            const normalized = {};
                            Object.keys(row).forEach((key) => {
                                const lowerKey = key.toLowerCase().trim();
                                normalized[lowerKey] = row[key]?.toString().trim() || '';
                            });

                            return {
                                email: normalized.email || normalized['e-mail'] || '',
                                lastname:
                                    normalized.lastname || normalized.nachname || normalized['last name'] || '',
                                firstname:
                                    normalized.firstname || normalized.vorname || normalized['first name'] || '',
                                unit: normalized.unit || normalized.einheit || normalized.abteilung || '',
                                dashboard: normalized.dashboard || '',
                            };
                        })
                        .filter((row) => row.email);
                }

                resolve({ users, hierarchy });
            } catch (error) {
                reject(new Error('Fehler beim Parsen der Excel-Datei: ' + error.message));
            }
        };

        reader.onerror = () => {
            reject(new Error('Fehler beim Lesen der Datei.'));
        };

        reader.readAsArrayBuffer(file);
    });
};

/**
 * Build a tree structure from hierarchical data
 * @param {Array} hierarchy - Array of { levels: [...] } objects
 * @returns {Object} - Tree structure with nodes
 */
const buildHierarchyTree = (hierarchy) => {
    const root = { id: 'root', name: 'Organisation', children: new Map(), level: -1, users: [] };

    hierarchy.forEach((row) => {
        let currentNode = root;

        row.levels.forEach((levelValue, levelIndex) => {
            if (!levelValue) return;

            const nodeId = `level-${levelIndex}-${levelValue}`;

            if (!currentNode.children.has(levelValue)) {
                currentNode.children.set(levelValue, {
                    id: nodeId,
                    name: levelValue,
                    level: levelIndex,
                    children: new Map(),
                    users: [],
                    parent: currentNode,
                });
            }

            currentNode = currentNode.children.get(levelValue);
        });
    });

    return root;
};

/**
 * Attach users to their respective units in the hierarchy tree
 * @param {Object} tree - The hierarchy tree
 * @param {Array} users - Array of user objects
 */
const attachUsersToTree = (tree, users) => {
    const unitNodeMap = new Map();

    const collectNodes = (node) => {
        if (node.name && node.name !== 'Organisation') {
            unitNodeMap.set(node.name.toLowerCase(), node);
        }
        node.children.forEach((child) => collectNodes(child));
    };
    collectNodes(tree);

    users.forEach((user) => {
        if (user.unit) {
            const unitNode = unitNodeMap.get(user.unit.toLowerCase());
            if (unitNode) {
                unitNode.users.push(user);
            }
        }
    });
};

/**
 * Convert tree to flat nodes/edges for ReactFlow (without positions - Dagre will handle layout)
 * Only unit leaders (users with dashboards) are shown inside hierarchy nodes
 * Regular employees are not displayed on the diagram
 * 
 * @param {Object} tree - Tree structure
 * @returns {{ nodes: Array, edges: Array }}
 */
const treeToFlowElements = (tree) => {
    const nodes = [];
    const edges = [];

    /**
     * Recursively traverse the tree and create nodes/edges
     * @param {Object} node - Current tree node
     * @param {string|null} parentId - Parent node ID for edge creation
     */
    const processNode = (node, parentId = null) => {
        const children = Array.from(node.children.values());
        const hasChildren = children.length > 0;

        // Skip root node but process its children
        if (node.id !== 'root') {
            // Identify unit leaders (users with dashboards assigned)
            const unitLeaders = node.users
                ? node.users.filter((user) => user.dashboard && user.dashboard.trim() !== '')
                : [];

            // Create hierarchy node with unit leaders embedded
            nodes.push({
                id: node.id,
                type: 'hierarchyNode',
                position: { x: 0, y: 0 }, // Position will be set by Dagre
                data: {
                    label: node.name,
                    level: node.level,
                    childCount: node.children.size,
                    userCount: node.users ? node.users.length : 0,
                    hasChildren: hasChildren,
                    unitLeaders: unitLeaders, // Unit leaders shown inside the card
                },
            });

            // Create edge from parent to this node
            if (parentId && parentId !== 'root') {
                edges.push({
                    id: `edge-${parentId}-${node.id}`,
                    source: parentId,
                    target: node.id,
                    type: 'smoothstep',
                    style: { stroke: '#c4cccc', strokeWidth: 2 },
                });
            }
            // Note: No separate user nodes are created - only unit leaders shown inside hierarchy nodes
        }

        // Process children recursively
        children.forEach((child) => {
            processNode(child, node.id);
        });
    };

    // Start processing from root
    processNode(tree);

    return { nodes, edges };
};

/**
 * Transform parsed org data into ReactFlow nodes and edges
 * Positions are NOT set here - use getLayoutedElements from layoutGraph.js
 *
 * @param {Object} orgData - Object with users array and hierarchy array
 * @returns {{ nodes: Array, edges: Array, stats: Object }} - ReactFlow nodes, edges, and statistics
 */
export const transformToFlowData = (orgData) => {
    const { users = [], hierarchy = [] } = orgData;

    // If we have hierarchy data, use that for the tree visualization
    if (hierarchy.length > 0) {
        const tree = buildHierarchyTree(hierarchy);

        // Attach users to their units
        if (users.length > 0) {
            attachUsersToTree(tree, users);
        }

        const { nodes, edges } = treeToFlowElements(tree);

        // Calculate statistics
        const levelStats = new Map();
        hierarchy.forEach((row) => {
            row.levels.forEach((value, level) => {
                if (value) {
                    if (!levelStats.has(level)) {
                        levelStats.set(level, new Set());
                    }
                    levelStats.get(level).add(value);
                }
            });
        });

        // Count users attached to units
        let usersAttached = 0;
        const countAttachedUsers = (node) => {
            usersAttached += node.users ? node.users.length : 0;
            node.children.forEach((child) => countAttachedUsers(child));
        };
        countAttachedUsers(tree);

        const stats = {
            totalUsers: users.length,
            totalUnits: nodes.filter((n) => n.type === 'hierarchyNode').length,
            usersWithUnit: usersAttached,
            usersWithDashboard: users.filter((u) => u.dashboard).length,
            usersWithoutUnit: users.length - usersAttached,
            levelCounts: Object.fromEntries(
                Array.from(levelStats.entries()).map(([level, set]) => [level, set.size])
            ),
            isHierarchical: true,
        };

        return { nodes, edges, stats };
    }

    // Fallback: use flat user-based visualization
    return transformUsersToFlowData(users);
};

/**
 * Transform flat user data into ReactFlow nodes and edges (fallback)
 * Positions are NOT set here - use getLayoutedElements from layoutGraph.js
 *
 * @param {Array} users - Array of user objects
 * @returns {{ nodes: Array, edges: Array, stats: Object }}
 */
const transformUsersToFlowData = (users) => {
    const nodes = [];
    const edges = [];
    const unitMap = new Map();

    const stats = {
        totalUsers: users.length,
        totalUnits: 0,
        usersWithUnit: 0,
        usersWithDashboard: 0,
        usersWithoutUnit: 0,
        isHierarchical: false,
    };

    // Group users by unit
    users.forEach((user) => {
        if (user.unit) {
            if (!unitMap.has(user.unit)) {
                unitMap.set(user.unit, []);
            }
            unitMap.get(user.unit).push(user);
            stats.usersWithUnit++;
        } else {
            stats.usersWithoutUnit++;
        }
        if (user.dashboard) {
            stats.usersWithDashboard++;
        }
    });

    stats.totalUnits = unitMap.size;

    // Create unit nodes and their user nodes
    let unitIndex = 0;
    unitMap.forEach((unitUsers, unitName) => {
        const unitId = `unit-${unitIndex}`;

        // Create unit node (no position - Dagre will handle it)
        nodes.push({
            id: unitId,
            type: 'unitNode',
            position: { x: 0, y: 0 },
            data: {
                label: unitName,
                userCount: unitUsers.length,
            },
        });

        // Create user nodes connected to this unit
        unitUsers.forEach((user, userIndex) => {
            const userId = `user-${unitIndex}-${userIndex}`;

            nodes.push({
                id: userId,
                type: 'userNode',
                position: { x: 0, y: 0 },
                data: {
                    email: user.email,
                    firstname: user.firstname,
                    lastname: user.lastname,
                    dashboard: user.dashboard,
                },
            });

            edges.push({
                id: `edge-${unitId}-${userId}`,
                source: unitId,
                target: userId,
                type: 'smoothstep',
                style: { stroke: '#c4cccc', strokeWidth: 2 },
            });
        });

        unitIndex++;
    });

    // Handle users without a unit
    const usersWithoutUnit = users.filter((user) => !user.unit);
    if (usersWithoutUnit.length > 0) {
        const noUnitId = 'unit-no-unit';

        nodes.push({
            id: noUnitId,
            type: 'unitNode',
            position: { x: 0, y: 0 },
            data: {
                label: 'Ohne Einheit',
                userCount: usersWithoutUnit.length,
                isSpecial: true,
            },
        });

        usersWithoutUnit.forEach((user, userIndex) => {
            const userId = `user-no-unit-${userIndex}`;

            nodes.push({
                id: userId,
                type: 'userNode',
                position: { x: 0, y: 0 },
                data: {
                    email: user.email,
                    firstname: user.firstname,
                    lastname: user.lastname,
                    dashboard: user.dashboard,
                },
            });

            edges.push({
                id: `edge-${noUnitId}-${userId}`,
                source: noUnitId,
                target: userId,
                type: 'smoothstep',
                style: { stroke: '#c4cccc', strokeWidth: 2 },
            });
        });
    }

    return { nodes, edges, stats };
};
